import type { TDataShape } from '../schemas/generated/client';
import type { Options } from '../schemas/generated';
import { reportAuthFlowBlocked } from '../observability/runtime-observability';
import { getApiAuthHandlers } from './http';

let loginRedirectPromise: Promise<void> | null = null;

const triggerLoginRedirect = () => {
  if (!loginRedirectPromise) {
    loginRedirectPromise = Promise.resolve()
      .then(() => getApiAuthHandlers().redirectToLogin())
      .catch(() => undefined)
      .finally(() => {
        loginRedirectPromise = null;
      });
  }

  return loginRedirectPromise;
};

export type AuthenticatedRequestOptions<TOptions> = TOptions extends { headers: infer THeaders }
  ? Omit<TOptions, 'headers'> & { headers?: Partial<THeaders> }
  : TOptions;

export type AuthenticatedOptions<
  TData extends TDataShape = TDataShape,
  ThrowOnError extends boolean = boolean,
> = AuthenticatedRequestOptions<Options<TData, ThrowOnError>>;

const createAuthenticatedOptions = <TOptions extends object>(
  options: AuthenticatedRequestOptions<TOptions>,
  idToken?: string,
): TOptions => {
  const currentHeaders: Record<string, unknown> =
    'headers' in options && typeof options.headers === 'object' && options.headers !== null
      ? options.headers
      : {};

  return {
    ...options,
    headers: {
      ...(idToken ? { 'X-Id-Token': `Bearer ${idToken}` } : {}),
      ...currentHeaders,
    },
  } as TOptions;
};

export function withAuthenticatedHeaders<
  TData extends TDataShape,
  ThrowOnError extends boolean = boolean,
>(options: AuthenticatedOptions<TData, ThrowOnError>): Promise<Options<TData, ThrowOnError>>;
export function withAuthenticatedHeaders<TOptions extends object>(
  options: AuthenticatedRequestOptions<TOptions>,
): Promise<TOptions>;
export async function withAuthenticatedHeaders<TOptions extends object>(
  options: AuthenticatedRequestOptions<TOptions>,
): Promise<TOptions> {
  const authSessionStore = getApiAuthHandlers().getAuthSession();

  if (authSessionStore.jwtToken && authSessionStore.idToken && !authSessionStore.isTokenExpired) {
    return createAuthenticatedOptions(options, authSessionStore.idToken);
  }

  reportAuthFlowBlocked({
    operation: 'authenticated-request',
    failureKind: 'missing-valid-token',
    tags: {
      hasJwtToken: Boolean(authSessionStore.jwtToken),
      hasIdToken: Boolean(authSessionStore.idToken),
      isTokenExpired: authSessionStore.isTokenExpired,
    },
  });
  void triggerLoginRedirect();
  throw new Error('Authenticated request requires a valid X-Id-Token.');
}
